import { useEffect, useMemo, useRef } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { isShareGoalCounterEligible } from '#src/app/pages/psf/share/shared/utils';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

/**
 * Share Goal Counter (CHANGE-97807): treats + fires `$exposure` the moment `SharingGoalFlow` mounts, so
 * the treated population equals the exposed population equals viewers of the counter's surface. Treating
 * at page load instead would also enrol desktop and `skip` viewers, who never get `SharingGoalFlow`, and
 * dilute the arms. Locale targeting stays in the FCM (en-US row).
 *
 * No explicit mobile-only or Super Recruiter check: `SharingGoalFlow` itself only renders for that
 * surface (`useShouldShowSharingGoalFlow`), so enrollment is already restricted to it. The initial-view
 * restriction lives in `isShareGoalCounterEligible`.
 */
export function useTreatShareGoalCounterExperiment(): void {
	const {
		data: {
			initial,
			experiments: { psfShareGoalCounterExperiment: experiment },
		},
	} = usePsfSharePageData();
	const utilityContext = useUtilityContext();
	const treatPromiseRef = useRef<Promise<void> | null>(null);

	// treatExperiment bypasses the config's enabledIf, so gate on the FCM flag here too — this is what
	// keeps the experiment a no-op while the kill-switch is off.
	// `!fallback` keeps treated == exposed: useTrackExperimentExposure refuses to fire $exposure on a
	// fallback resolution (kill-switch, error, bot, invalid name), so treating one would enroll a session
	// that can never expose.
	const isEligible = isShareGoalCounterEligible({ experiment, initial });

	// treatExperiment registers treatment server-side, but the resolved object stays treated:false, and
	// useTrackExperimentExposure no-ops on !treated — so the exposure object is armed with treated:true
	// from mount. Safe only because the sole call site below gates on treatExperiment's own return
	// (server-confirmed registration). Do not call trackExposure from anywhere else.
	const experimentForExposure = useMemo(() => ({ ...experiment, treated: true }), [experiment]);
	const trackExposure = useTrackExperimentExposure(experimentForExposure);

	useEffect(() => {
		if (!isEligible) return;
		// One-way latch holding the in-flight promise, never reset, so a re-render or a re-run of this
		// effect can't leak a second treat and double-count one supporter.
		treatPromiseRef.current ??= (async () => {
			// `treatExperiment` resolves `false` rather than throwing when the treatment did not land
			// server-side (network failure, bot session, not-yet-treatable assignment), so $exposure is gated
			// on that return: firing it for an unregistered treat would expose a session
			// experiment_variations has no record of.
			if (await treatExperiment(utilityContext, experiment.experimentName)) {
				await trackExposure().catch(() => undefined);
			}
		})();
	}, [isEligible, experiment.experimentName, utilityContext, trackExposure]);
}
